import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import "./ManageShopifyApp.css";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import { HelenService } from "../../services/helen/helenService";
import { ShopifyService } from "../../services/shopify/shopifyService";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  appId: number;
}

const ManageShopifyModal: React.FC<Props> = ({ isOpen, onClose, appId }) => {
  const { showToast } = useToast();
  const [appName, setAppName] = useState("");
  const [bots, setBots] = useState<any[]>([]);
  const [selectedBot, setSelectedBot] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    HelenService.getBotsAssignedToUser("all")
      .then((res) => {
        if (res.status === 200 && res.data?.userAssignedBots) {
          setBots(res.data.userAssignedBots);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !appId) return;
    setIsLoading(true);
    ShopifyService.getAppById(appId)
      .then((res) => {
        const data = res.data;
        if (data) {
          setAppName(data.shop_domain || "");
          setSelectedBot(String(data.bot_id || ""));
          setSelectedStoreId(String(data.id || ""));
        }
      })
      .catch(() => showToast("Failed to load app details", "error"))
      .finally(() => setIsLoading(false));
  }, [isOpen, appId]);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!selectedBot) errs.selectedBot = "Please select a Helen";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await ShopifyService.updateApp(selectedStoreId, Number(selectedBot));
      showToast("App updated successfully", "success");
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Failed to update app. Please try again.";
      showToast(msg, "error");
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="manage-shopify-overlay" onClick={onClose}>
      <div className="manage-shopify-modal" onClick={(e) => e.stopPropagation()}>
        <div className="manage-shopify-modal-header">
          <h2 className="manage-shopify-modal-title">Edit Shopify App</h2>
          <button className="manage-shopify-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="manage-shopify-modal-body">
          {isLoading ? (
            <p style={{ textAlign: "center", color: "#8b92a9" }}>Loading...</p>
          ) : (
            <form className="edit-shopify-app-form-grid" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Store Domain<span className="required"> *</span></label>
                <input
                  type="text"
                  placeholder="Enter app name"
                  value={appName}
                  disabled={true}
                  onChange={(e) => setAppName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Assign Helen<span className="required"> *</span></label>
                <select value={selectedBot} onChange={(e) => setSelectedBot(e.target.value)}>
                  <option value="">Select Helen</option>
                  {bots.map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot?.helen_name
                        ? bot.helen_name.charAt(0).toUpperCase() + bot.helen_name.slice(1).replace(/_/g, " ")
                        : "Unknown"}
                    </option>
                  ))}
                </select>
                {errors.selectedBot && <span className="error-text">{errors.selectedBot}</span>}
              </div>

              <div className="form-buttons">
                <button className="cancel-button" type="button" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="submit-button">
                  Update
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ManageShopifyModal;
